/**
 * The one model shape an authoring UI may produce: person-hours per year, as
 * `people × hours/week × weeks/year`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS RATHER THAN A FORM BUILDING ITS OWN FORMULA
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `computeRoleCoverage` gates BOTH sides of the ratio on `isPersonHours` —
 * `coverage-ratio.ts` refuses a numerator or a denominator that reduces to any
 * other dimension, and reports `WORKLOAD_WRONG_DIMENSION` /
 * `IMPACT_WRONG_DIMENSION`. Those are honest states and the UI renders them as
 * prose, but a screen that can PRODUCE one is a screen that lets a reader
 * author a model the engine will refuse, with no way to tell from the form that
 * it happened.
 *
 * So the authoring surface never chooses a dimension. It collects three plain
 * numbers and this function turns them into the only vector the engine accepts.
 * The guarantee is the BUILDER's, not a check here: `b.mul` computes the
 * result's exponents at the type level, so `HOURS × DIMENSIONLESS × PEOPLE` is
 * known to be `time^1·person^1` at compile time. Change any of the three
 * dimensions below and this file stops compiling — it does not start producing
 * a model the engine rejects at runtime.
 *
 * ── WHY THE SAME SHAPE FOR A WORKLOAD AND FOR AN IMPACT ─────────────────────
 *
 * They are the denominator and the numerator of one ratio, and
 * `coverage-ratio.ts` requires them to share a dimension. Two shapes would be
 * two chances to disagree. The labels differ because the sentences differ — a
 * workload's headcount is "people in the role" and an impact's is "people it
 * covers" — and the labels are what `CoverageAssumptionsTable` prints under the
 * percentage, so they are the caller's to supply and are stored verbatim.
 *
 * ── WHY THERE IS NO `perPeriod` HERE ────────────────────────────────────────
 *
 * `weeksPerYear` annualises explicitly, which is the shape
 * `coverage-worked-example.test.ts` builds and the shape a reader can check on
 * paper against the row labels. A `perPeriod: "week"` binding reaches the same
 * number with the weeks input absent — correct, and it deletes the row that
 * makes the arithmetic legible in the assumptions table. The table is the
 * reason the response carries the model at all, so the explicit form wins here.
 */
import { CoverageDimensions } from "./coverage-dimension.types";
import { buildCoverageFormula } from "./coverage-formula.builder";
import type { ValidatedCoverageFormula } from "./coverage-formula.schema";
import { COVERAGE_PERIOD_BASIS_ABSOLUTE } from "./coverage-period.types";

/** The three magnitudes, and the words that go beside them in the table. */
export interface PersonHoursPerYearFactors {
  /** How many people. `PEOPLE`. */
  readonly headcount: number;
  /** Contracted or returned hours, per person, per week. `HOURS`. */
  readonly hoursPerWeek: number;
  /** Weeks actually worked in a year. Dimensionless. */
  readonly weeksPerYear: number;
  /**
   * What each row is called in `CoverageAssumptionsTable`.
   *
   * Supplied by the caller because only the caller has a locale, and stored
   * verbatim because the table reads the STORED model rather than re-deriving
   * the words. A model authored in French keeps its French row labels.
   */
  readonly labels: {
    readonly headcount: string;
    readonly hoursPerWeek: string;
    readonly weeksPerYear: string;
  };
}

/**
 * `headcount × hoursPerWeek × weeksPerYear`, as a validated model whose
 * dimension is `PERSON_HOURS` by construction.
 *
 * Every magnitude reaches the model unrounded and unclamped. A zero or a
 * negative is NOT refused here: the engine already names both
 * (`WORKLOAD_ZERO_HOURS`, `WORKLOAD_NEGATIVE_HOURS`), and refusing them in two
 * places is two places to disagree about what a valid model is. The FORM
 * refuses them before calling this, which is where a reader can be told why.
 *
 * @throws {CoverageFormulaBuilderError} only on a bug in the builder itself —
 * every input here is a plain finite number and the shape is fixed.
 */
export function personHoursPerYearFormula(
  factors: PersonHoursPerYearFactors
): ValidatedCoverageFormula<typeof COVERAGE_PERIOD_BASIS_ABSOLUTE> {
  return buildCoverageFormula((b) => {
    const hoursPerWeek = b.input({
      key: "hoursPerWeek",
      value: factors.hoursPerWeek,
      dimension: CoverageDimensions.HOURS,
      timeUnit: "hour",
      label: factors.labels.hoursPerWeek
    });
    const weeksPerYear = b.input({
      key: "weeksPerYear",
      value: factors.weeksPerYear,
      dimension: CoverageDimensions.DIMENSIONLESS,
      label: factors.labels.weeksPerYear
    });
    const headcount = b.input({
      key: "headcount",
      value: factors.headcount,
      dimension: CoverageDimensions.PEOPLE,
      label: factors.labels.headcount
    });

    const hoursPerYear = b.mul(hoursPerWeek, weeksPerYear);
    return b.mul(hoursPerYear, headcount);
  });
}
